// Call Records: every voice call to the agent, newest first, with what came of it. A call that booked
// shows the bookings it made (and only those); a call that asked for a teammate shows the reason; any
// other call is an enquiry, shown with its summary. Clicking a row opens the recording, summary and
// transcript.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FaPhoneAlt, FaCalendarCheck, FaHeadset, FaQuestionCircle, FaMicrophone, FaPhoneSlash } from 'react-icons/fa';
import api, { formatDate, formatDateTime, formatPhone, formatTime } from '../services/api';
import RecordingModal, { formatDuration } from '../components/RecordingModal';
import { BranchTag, useEngagementScope } from '../context/EngagementScope';
import {
  DateRange,
  InterventionBadge,
  Loader,
  Pagination,
  SearchBox,
  StatCard,
  StatGrid,
  ToggleChip,
  VoiceButton,
  inDateRange,
  usePagination,
} from '../components/ui';

const BASE_COLUMNS = ['S No', 'Call Time', 'Phone Number', 'Customer', 'Outcome', 'What Happened', 'Language', 'Duration'];

export const OUTCOMES = {
  booked: { label: 'Booked', tone: 'ok' },
  callback: { label: 'Teammate callback', tone: 'warn' },
  enquiry: { label: 'Enquiry', tone: 'info' },
  no_conversation: { label: 'No conversation', tone: 'muted' },
};

export function OutcomePill({ outcome }) {
  const cfg = OUTCOMES[outcome] || OUTCOMES.enquiry;
  return <span className={`de-pill de-pill--${cfg.tone}`}>{cfg.label}</span>;
}

function WhatHappened({ call }) {
  if (call.outcome === 'booked') {
    return (
      <div>
        {(call.bookings || []).map((b) => (
          <div key={b.id} style={{ whiteSpace: 'nowrap' }}>
            <span className="de-strong">{b.service_name}</span>
            <span className="de-muted"> · {formatDateTime(b.starts_at)} · {b.staff_name || 'Any stylist'}</span>
          </div>
        ))}
      </div>
    );
  }
  if (call.outcome === 'no_conversation' && !call.summary) {
    return <div className="de-muted">Call ended before a conversation started.</div>;
  }
  const text = call.outcome === 'callback' ? call.callback_reason || call.summary : call.summary;
  return (
    <div className="de-muted" style={{ maxWidth: 380, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={text}>
      {text || 'No summary yet'}
    </div>
  );
}

export default function Calls() {
  const { multiBranch, isHQ } = useEngagementScope();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [applied, setApplied] = useState('');
  const [outcome, setOutcome] = useState('');
  const [withRecording, setWithRecording] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recording, setRecording] = useState(null);

  const load = (silent) => {
    if (!silent) setLoading(true);
    return api
      .listCalls()
      .then(setRows)
      .catch((e) => !silent && toast.error(e.message || 'Failed to load call records'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, []);

  const callRows = Array.isArray(rows) ? rows : [];
  const inRange = useMemo(
    () => callRows.filter((r) => inDateRange(r.call_at || r.created_at, dateFrom, dateTo)),
    [callRows, dateFrom, dateTo],
  );

  const filtered = useMemo(() => {
    const q = applied.replace(/\s+/g, '').toLowerCase();
    return inRange.filter((r) => {
      if (outcome && r.outcome !== outcome) return false;
      if (withRecording && !r.recording_url) return false;
      if (!q) return true;
      return String(r.customer_phone || '').includes(q) || String(r.customer_name || '').toLowerCase().replace(/\s+/g, '').includes(q);
    });
  }, [inRange, applied, outcome, withRecording]);

  const { page, setPage, pageSize, setPageSize, paginated, totalPages, offset } = usePagination(filtered);

  const count = (key) => inRange.filter((r) => r.outcome === key).length;
  // Recordings are not kept yet: the column, tile and filter appear once any call has one.
  const hasRecordings = rows.some((r) => r.recording_url);
  const COLUMNS = hasRecordings ? [...BASE_COLUMNS, 'Recording'] : BASE_COLUMNS;
  const stats = [
    { key: '', label: 'Total Calls', value: inRange.length, icon: <FaPhoneAlt /> },
    { key: 'booked', label: 'Booked', value: count('booked'), icon: <FaCalendarCheck /> },
    { key: 'callback', label: 'Teammate Callback', value: count('callback'), icon: <FaHeadset /> },
    { key: 'enquiry', label: 'Enquiry', value: count('enquiry'), icon: <FaQuestionCircle /> },
    { key: 'no_conversation', label: 'No conversation', value: count('no_conversation'), icon: <FaPhoneSlash /> },
  ];

  return (
    <div className="de-fade">
      <PageTitle />

      <StatGrid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', marginBottom: 28 }}>
        {stats.map((s) => (
          <StatCard key={s.label} label={s.label} value={s.value} icon={s.icon} active={outcome === s.key} onClick={() => setOutcome(s.key)} />
        ))}
        {hasRecordings ? (
          <StatCard
            label="With Recording"
            value={inRange.filter((r) => r.recording_url).length}
            icon={<FaMicrophone />}
            active={withRecording}
            onClick={() => setWithRecording((v) => !v)}
          />
        ) : null}
      </StatGrid>

      <div className="de-row de-row--between de-row--wrap" style={{ gap: 12, marginBottom: 14 }}>
        <div className="de-row de-row--wrap" style={{ gap: 10, flex: 1 }}>
          <SearchBox value={phone} onChange={setPhone} placeholder="Search phone number or name..." onEnter={() => setApplied(phone)} />
          {Object.entries(OUTCOMES).map(([key, cfg]) => (
            <ToggleChip key={key} active={outcome === key} onClick={() => setOutcome(outcome === key ? '' : key)}>
              {cfg.label}
            </ToggleChip>
          ))}
          {hasRecordings ? (
            <ToggleChip active={withRecording} onClick={() => setWithRecording((v) => !v)}>
              With recording
            </ToggleChip>
          ) : null}
          <button type="button" className="de-btn de-btn--primary" onClick={() => setApplied(phone)}>
            Search
          </button>
        </div>
        <button type="button" className="de-btn de-btn--outline" onClick={() => load(false)}>
          Refresh
        </button>
      </div>

      <div className="de-panel" style={{ padding: '14px 18px', marginBottom: 20 }}>
        <DateRange
          from={dateFrom}
          to={dateTo}
          onFrom={setDateFrom}
          onTo={setDateTo}
          onClear={() => {
            setDateFrom('');
            setDateTo('');
          }}
        />
        <div className="de-hint">Every phone call to the voice agent. Open a call for its summary and full transcript.</div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="de-table-shell">
          <div className="de-table-wrap">
            <table className="de-table" style={{ minWidth: 1180 }}>
              <thead>
                <tr>
                  {COLUMNS.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="de-table-empty">
                      No calls found
                    </td>
                  </tr>
                ) : (
                  paginated.map((r, i) => (
                    <tr key={r.id} className="de-row-click" onClick={() => setRecording(r)}>
                      <td className="de-td-center de-td-muted">{offset + i + 1}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div>{formatDate(r.call_at || r.created_at)}</div>
                        <div className="de-muted">{formatTime(r.call_at || r.created_at)}</div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatPhone(r.customer_phone) || '—'}</td>
                      <td className="de-td-strong">
                        {r.customer_name || <span className="de-muted">New caller</span>}
                        {multiBranch || (isHQ && r.queue) ? (
                          <div style={{ marginTop: 3 }}>
                            <BranchTag franchiseId={r.franchise_id} name={r.branch_name} queue={r.queue} />
                          </div>
                        ) : null}
                        {r.handoff_open ? (
                          <div style={{ marginTop: 3 }}>
                            <InterventionBadge compact />
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <OutcomePill outcome={r.outcome} />
                      </td>
                      <td>
                        <WhatHappened call={r} />
                      </td>
                      <td>{r.language || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDuration(r.duration_seconds) || '—'}</td>
                      {hasRecordings ? (
                        <td className="de-td-center" onClick={(e) => e.stopPropagation()}>
                          {r.recording_url ? <VoiceButton onClick={() => setRecording(r)} /> : <span className="de-muted">—</span>}
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
        </div>
      )}

      {recording ? <RecordingModal call={recording} onClose={() => setRecording(null)} /> : null}
    </div>
  );
}

function PageTitle() {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="de-page-title">Call Records</div>
      <div className="de-page-sub">Every voice call: who called, what came of it, the summary and transcript.</div>
    </div>
  );
}
